import React from 'react';
import { RankedBarChart, OrdinalColumnChart, ShareBar, TrendLine, StackedColumnChart } from './charts';
import { formatCurrency, formatNumber } from './chartTheme';
import { alignSeries } from './graphData';

/**
 * Card definitions shared by the Graphs page and the admin dashboard, so a graph
 * ticked "Show on dashboard" renders there exactly as it does on the board.
 */

const UNIT_SERIES_LABEL = {
  currency: 'Paid',
  patients: 'Patients',
  shipments: 'Shipments',
  records: 'Records',
  requests: 'Requests',
  submissions: 'Submissions',
  tests: 'Tests',
  visits: 'Visits',
  appointments: 'Appointments',
};

const SUBTITLE = {
  'interactions.categories': 'Every website and enquiry form in the range',
};

const UNKNOWN_SUFFIX = {
  age: 'without a date of birth',
  weight: 'without a weight',
  height: 'without a height',
  caseRating: 'without a rating',
};

// Search synonyms for the terms staff actually type, which are not always the
// words in the metric label.
const KEYWORDS = {
  'trend.fees': 'revenue money income collection earnings',
  topPayers: 'revenue money fees billing highest',
  byDoctor: 'doctor consultant workload',
  maritalStatus: 'family single married',
  newVsFollowUp: 'repeat returning visit',
  caseFlags: 'before after case study review marketing',
  treatmentDuration: 'retention how long months',
  'courier.cities': 'delivery dispatch shipment city',
  'courier.providers': 'delivery partner shipment',
  'courier.status': 'delivery delivered pending transit',
  'courier.topPatients': 'delivery dispatch patient frequent repeat',
  'referral.centres': 'reference test doctor lab pathology diagnostic referred to centre',
  'referral.tests': 'reference test pathology blood investigation advised',
  package: 'health plan subscription',
  area: 'mumbai locality',
  'courier.trend': 'shipments by month delivery dispatch monthly',
  'trend.feesByMode': 'payment mode cash upi card online revenue collection monthly',
  'trend.newVsFollowUp': 'new follow up repeat returning visit monthly',
  'trend.appointmentChannel': 'appointment channel online in-clinic walk-in phone monthly',
  'appointment.bookingLocation': 'appointment booking location branch where booked',
  'appointment.bookedBy': 'staff booked by receptionist who booked appointment',
  'appointment.sourcePage': 'appointment source page website form where from',
  'interactions.sourcePage': 'website source page form leads where from',
  'interactions.categories':
    'website leads callback contact us testimonial questions thoughts enquiry forms',
};

export const SHAPE_HEIGHT = { series: 300, share: 200, ranked: 300, ordinal: 280, multiSeries: 320 };

// Cards built from more than one metric. Their id is not a metric key, so
// anything that needs to fetch a card (the dashboard) looks its sources up here.
const REGISTRATIONS_TREND = 'trend.registrations';
const COMPOSED_SOURCES = {
  [REGISTRATIONS_TREND]: ['trend.patients', 'trend.prescriptions'],
};

/** The metric keys a card id is drawn from. */
export const sourcesFor = (cardId) => COMPOSED_SOURCES[cardId] || [cardId];

/**
 * A metric that carries several series (`multi: true` in the catalogue) comes
 * back as `{ keys, labels, data: monthTotals, datasets: [{ label, data, color? }], stacked? }`
 * (`series` is accepted as an alias of `datasets`). Returns null for an
 * ordinary single-series metric.
 */
export const datasetsOf = (metric) => {
  const sets = Array.isArray(metric?.datasets)
    ? metric.datasets
    : Array.isArray(metric?.series) && metric.series.every((set) => set && typeof set === 'object' && !Array.isArray(set))
      ? metric.series
      : null;
  if (!sets || !sets.length) return null;
  return sets.map((set, index) => ({
    label: String(set.label ?? set.name ?? `Series ${index + 1}`),
    data: Array.isArray(set.data) ? set.data.map((value) => Number(value) || 0) : [],
    ...(set.color ? { color: set.color } : {}),
  }));
};

const isStacked = (metric) => metric?.stacked === true || metric?.shape === 'stacked';
const isMulti = (metric) => Boolean(datasetsOf(metric)) || metric?.multi === true || metric?.shape === 'stacked';

const subtitleFor = (metric) => {
  const unit = metric.unit || 'patients';
  if (SUBTITLE[metric.key]) return SUBTITLE[metric.key];
  if (metric.description) return metric.description;
  if (metric.unknown) {
    return `${formatNumber(metric.unknown)} ${UNKNOWN_SUFFIX[metric.key] || 'without a value'}`;
  }
  if (datasetsOf(metric)) {
    return isStacked(metric)
      ? 'Monthly totals, stacked - each colour is one series'
      : 'Monthly totals per series';
  }
  if (metric.shape === 'series') return 'Monthly totals across the range';
  // Folded onto the DropDowns page list: every option, in its configured order.
  if (metric.fromOptions) return 'Every option on the DropDowns page, in the same order';
  if (metric.shape === 'share') return 'Share of records with this field filled';
  if (metric.shape === 'flags') return 'Patients carrying each mark';
  if (metric.shape === 'ordinal') return 'Grouped into bands';
  return `Top 10 by ${unit === 'currency' ? 'amount' : unit.replace(/s$/, '')} count`;
};

const viewFor = (metric) => {
  const labels = metric.labels || [];
  const data = metric.data || [];
  const seriesLabel = metric.seriesLabel || UNIT_SERIES_LABEL[metric.unit] || 'Records';
  const valueFormatter = metric.unit === 'currency' ? formatCurrency : formatNumber;
  const multi = datasetsOf(metric);

  if (multi) {
    const node = isStacked(metric) ? (
      <StackedColumnChart labels={labels} datasets={multi} valueFormatter={valueFormatter} />
    ) : (
      <TrendLine labels={labels} datasets={multi} valueFormatter={valueFormatter} />
    );
    return { labels, datasets: multi, valueFormatter, node };
  }

  const datasets = [{ label: seriesLabel, data }];
  let node;
  if (metric.shape === 'series') {
    node = <TrendLine labels={labels} datasets={datasets} valueFormatter={valueFormatter} />;
  } else if (metric.shape === 'share') {
    node = <ShareBar labels={labels} data={data} valueFormatter={valueFormatter} />;
  } else if (metric.shape === 'ordinal') {
    node = (
      <OrdinalColumnChart labels={labels} data={data} seriesLabel={seriesLabel} valueFormatter={valueFormatter} />
    );
  } else {
    node = <RankedBarChart labels={labels} data={data} seriesLabel={seriesLabel} valueFormatter={valueFormatter} />;
  }
  return { labels, datasets, valueFormatter, node };
};

/**
 * Every card the board can show. Driven by the catalogue when there is one, so
 * a metric that is empty, failed or forbidden still gets its card - an absent
 * graph reads as "we do not track this", which is a different claim entirely.
 * Without a catalogue (the dashboard fetches only what it shows) the metrics
 * themselves describe the cards.
 */
export const buildCards = (catalogue = [], metrics = {}) => {
  const order = catalogue.length ? catalogue.map((entry) => entry.key) : Object.keys(metrics);
  const cards = [];
  const used = new Set(['totals.patients', 'totals.prescriptions']);
  const describes = (key) => catalogue.find((item) => item.key === key) || metrics[key] || { key };

  const trendSources = COMPOSED_SOURCES[REGISTRATIONS_TREND];
  if (trendSources.every((key) => order.includes(key) || metrics[key])) {
    trendSources.forEach((key) => used.add(key));
    cards.push({
      id: REGISTRATIONS_TREND,
      group: 'Activity over time',
      title: 'Registrations and prescriptions by month',
      subtitle: 'Both series are counts, so they share one scale',
      keywords: 'trend monthly new patients prescriptions line',
      span: 2,
      height: 300,
      sources: trendSources,
      render: (rows) => {
        const { labels, series } = alignSeries(rows.map((row) => row || {}));
        const datasets = [
          { label: 'New patients', data: series[0] || [] },
          { label: 'Prescriptions', data: series[1] || [] },
        ];
        return { labels, datasets, node: <TrendLine labels={labels} datasets={datasets} /> };
      },
    });
  }

  order.forEach((key) => {
    if (used.has(key)) return;
    const entry = describes(key);
    if (entry.shape === 'scalar') return;

    const base = { ...entry, ...(metrics[key] || {}), key };
    const multi = isMulti(base);
    cards.push({
      id: key,
      group: base.group || 'Other',
      title: base.label || key,
      subtitle: subtitleFor(base),
      keywords: KEYWORDS[key] || base.keywords || '',
      span: multi ? 2 : undefined,
      height: multi ? SHAPE_HEIGHT.multiSeries : SHAPE_HEIGHT[base.shape] || 280,
      sources: [key],
      render: ([row]) => {
        const current = { ...entry, ...(row || {}), key };
        return { subtitle: subtitleFor(current), ...viewFor(current) };
      },
    });
  });

  return cards;
};
